import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { sessionStore } from "../../../../../contexts/SessionContext";
import {
    EditorBody,
    EditorContainer,
    EditorFooter,
    EditorMainContent,
    EditorPanel,
} from "../Components/EditorLayout/EditorLayout";
import { LegacyLoader } from "../../../../../components/Loader/Loader";
import { defineMessages, useIntl } from "react-intl";
import { commonMessages, pageTitles } from "../../../../../utils/messages";
import { useUrl } from "../../../../../hooks/usePage";
import { Pagetype } from "../../../../../interfaces/Config";
import {
    HierarchyPath,
    TreeNode,
    checkOnAllBranches,
    nodePathsToUniqueLeafId,
    useNodeCheckingStrategy,
} from "../../../../../components/TreeCheckboxGroup";
import { useDismissableMessage } from "../../../../../components/MessageDismiss/MessageDismiss";
import { DismissableMessages } from "../../../../../interfaces/Session";
import { currentlyChecked, originallyCheckedStudentsInPath } from "./utils";
import ConfirmationModal from "./Components/ConfirmationModal";
import MobileContainer from "../EditPlaylist/TeacherPlaylistSend/Components/MobileContainer";
import { playlistMessages } from "../EditPlaylist/playlistMessages";
import TeacherPlaylistSend from "../EditPlaylist/TeacherPlaylistSend/TeacherPlaylistSend";
import playlistReducer from "../EditPlaylist/playlistReducer";
import {
    getInitPlaylistAction,
    initCheckedStudentsWithAssignment,
    isPlaylistNameUnique,
    prepareClassroomsForTreeCheckboxGroup,
} from "../EditPlaylist/utils";
import TeacherPlaylistDescribe from "../EditPlaylist/TeacherPlaylistDescribeAndSend/TeacherPlaylistDescribe";
import {
    useAllPlaylistsQuery,
    usePlaylistAssignmentsQuery,
    usePlaylistQuery,
} from "../../api/useQueries";
import {
    useCreateAssignmentMutation,
    useUpdateAssignmentMutation,
    useUpdatePlaylistMutation,
} from "../../api/useMutations";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { useQueryClient } from "@tanstack/react-query";
import queryKeys from "../../api/queryKeys";
import { useUserId } from "../../../../../hooks/useUserInfo";
import TabTitle from "../../../../../components/TabTitle/TabTitle";
import { useGroups } from "../../../../DashBoard/DashboardTeacher/Group/api/useQueries";
import { useAllSubjectsData } from "../../../../../hooks/useData";
import { isLoading } from "../../../../../hooks/queries/utils";
import { usePlaylistableCollections } from "../../api/usePlaylistableCollections";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";

import "./EditAssignment.scss";

const EditAssignment = () => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const userId = useUserId();
    const history = useHistory();
    const data = useAllSubjectsData();
    const { data: collections, queries: collectionQueries } =
        usePlaylistableCollections();
    const {
        session: { classrooms },
    } = useContext(sessionStore);
    const { isTabletOrSmaller } = useBreakpoints();
    const playlistModeUrl = useUrl(Pagetype.WORKMODE_PLAYLIST);
    const showConfirmation = !useDismissableMessage(
        DismissableMessages.playlistRemoveStudentFromRecipients
    );
    const { playlistId } = useParams<{ playlistId: string }>();

    const assignmentsResult = usePlaylistAssignmentsQuery(playlistId);
    const playlistResult = usePlaylistQuery(playlistId);
    const allPlaylistsResult = useAllPlaylistsQuery();

    const [playlist, dispatch] = useReducer(playlistReducer, undefined);
    const queryClient = useQueryClient();

    const initActionDone = useRef(false);
    useEffect(() => {
        if (
            initActionDone.current ||
            playlist ||
            playlistResult.isLoading ||
            collectionQueries.some(isLoading)
        )
            return;

        const initAction = getInitPlaylistAction(
            playlistId,
            playlistResult,
            allPlaylistsResult,
            { data, collections },
            intl
        );
        dispatch(initAction);
        initActionDone.current = true;
    }, [
        playlistResult,
        playlist,
        playlistId,
        allPlaylistsResult,
        intl,
        data,
        collections,
        collectionQueries,
    ]);

    const groupsQuery = useGroups();
    const groups = groupsQuery.data;
    const students = useMemo<Record<string, TreeNode>>(
        () =>
            prepareClassroomsForTreeCheckboxGroup([
                ...(classrooms ?? []),
                ...(groups ?? []),
            ]),
        [classrooms, groups]
    );

    const {
        checkedLeaves: checkedStudents,
        toggleNode: toggleStudents,
        setCheckedLeaves: setCheckedStudents,
        dirty: assignmentIsDirty,
    } = useNodeCheckingStrategy(students ?? {}, checkOnAllBranches);
    // Set initially checked students from the assignment data
    const initialized = useRef<boolean>(false);
    useEffect(() => {
        if (initialized.current === true || !assignmentsResult.data) return;
        if (assignmentsResult.data.length > 0) {
            const checkedStudents = initCheckedStudentsWithAssignment(
                assignmentsResult.data[0],
                classrooms
            );
            setCheckedStudents(checkedStudents, false);
        }
        initialized.current = true;
    }, [assignmentsResult.data, setCheckedStudents, students, classrooms]);

    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const toggledPathRef = useRef<string | undefined>(undefined);
    const togglePathHandler = useCallback(
        (path: HierarchyPath) => {
            if (!classrooms) return;
            if (!showConfirmation) {
                toggleStudents(path);
                return;
            }

            const studentsInOriginalAssignment =
                originallyCheckedStudentsInPath(
                    path,
                    assignmentsResult.data?.[0]?.students.map(
                        (stud) => stud.id
                    ) ?? [],
                    classrooms
                ).filter(currentlyChecked(checkedStudents));
            if (studentsInOriginalAssignment.length > 0) {
                setConfirmationModalOpen(true);
                toggledPathRef.current = path;
            } else {
                toggleStudents(path);
            }
        },
        [
            toggleStudents,
            assignmentsResult.data,
            classrooms,
            checkedStudents,
            showConfirmation,
        ]
    );

    const {
        mutateAsync: mutateUpdateAssignment,
        isLoading: isMutateUpdateAssignmentLoading,
    } = useUpdateAssignmentMutation();
    const {
        mutateAsync: mutateCreateAssignment,
        isLoading: isMutateCreateAssignment,
    } = useCreateAssignmentMutation();
    const {
        mutateAsync: mutatePlaylist,
        isLoading: isMutateUpdatePlaylistLoading,
    } = useUpdatePlaylistMutation();
    const isSaving =
        isMutateUpdateAssignmentLoading ||
        isMutateCreateAssignment ||
        isMutateUpdatePlaylistLoading;

    if (!playlistResult.data || !playlist) return <LegacyLoader />;

    const canSavePlaylist = isPlaylistNameUnique(
        allPlaylistsResult.data,
        playlist
    );
    // TODO: warn user to choose a different name
    const saveChangesHandler = async () => {
        if (
            !assignmentsResult.data ||
            !playlist ||
            (!playlist.dirty && !assignmentIsDirty)
        )
            return;

        try {
            const promises: Promise<any>[] = [];
            if (playlist.dirty && canSavePlaylist)
                promises.push(mutatePlaylist(playlist));
            if (assignmentIsDirty) {
                let assignmentPromise;
                if (assignmentsResult.data.length > 0)
                    assignmentPromise = mutateUpdateAssignment({
                        ...assignmentsResult.data[0],
                        students: nodePathsToUniqueLeafId(
                            checkedStudents,
                            students
                        ),
                    });
                else
                    assignmentPromise = mutateCreateAssignment({
                        students: nodePathsToUniqueLeafId(
                            checkedStudents,
                            students
                        ),
                        teacher: userId,
                        assigned_resource: {
                            resource_id: playlistId,
                        },
                    });
                promises.push(assignmentPromise);
                const updateProgressPromise = queryClient.invalidateQueries({
                    queryKey: queryKeys.getPlaylistProgress({
                        teacher_id: userId,
                    }),
                    exact: true,
                });
                promises.push(updateProgressPromise);
            }
            await Promise.all(promises);
            history.push(playlistModeUrl);
        } catch (err) {
            // Ignore (error handling in hooks)
        }
    };

    return (
        <>
            <TabTitle
                title={intl.formatMessage(
                    pageTitles.teacherPlaylistEditAssignment,
                    { playlist: playlist.name }
                )}
            />

            <EditorContainer className="edit-assignment">
                <EditorBody>
                    <EditorPanel
                        title={{ content: playlistResult.data.name }}
                        backTo={playlistModeUrl}
                    >
                        <TeacherPlaylistSend
                            students={students}
                            checkedStudents={checkedStudents}
                            toggleStudents={togglePathHandler}
                        />
                        {isTabletOrSmaller && (
                            <MobileContainer
                                label={intl.formatMessage(
                                    playlistMessages.playlistDescribeMobileLabel
                                )}
                                className="edit-playlist__mobile-describe"
                            >
                                <TeacherPlaylistDescribe
                                    playlist={playlist}
                                    dispatch={dispatch}
                                />
                            </MobileContainer>
                        )}
                    </EditorPanel>
                    <EditorMainContent>
                        <TeacherPlaylistDescribe
                            playlist={playlist}
                            dispatch={dispatch}
                        />
                    </EditorMainContent>
                </EditorBody>
                <EditorFooter
                    button1={{
                        label: intl.formatMessage(commonMessages.cancel),
                        onPress: () => history.goBack(),
                    }}
                    button2={{
                        label: intl.formatMessage(messages.saveChanges),
                        onPress: saveChangesHandler,
                        disabled: !isOnline,
                        loading: isSaving,
                    }}
                />
            </EditorContainer>

            <ConfirmationModal
                open={confirmationModalOpen}
                onClose={() => setConfirmationModalOpen(false)}
                onOk={() => {
                    if (!toggledPathRef.current) return;
                    const path = toggledPathRef.current;
                    toggleStudents(path);
                    toggledPathRef.current = undefined;
                }}
                onCancel={() => {
                    toggledPathRef.current = undefined;
                }}
                checkedStudents={checkedStudents}
                originallyCheckedStudents={assignmentsResult.data?.[0]?.students.map(
                    (student) => student.id
                )}
                toggledPath={toggledPathRef.current}
            />
        </>
    );
};
export default EditAssignment;

const messages = defineMessages({
    saveChanges: {
        id: "playlist-editAssignment-saveChanges",
        defaultMessage: "Save changes",
    },
});
